import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Cpu, Sparkles, X, Search, Globe, Menu, Users, Play, 
  Download, ChevronDown, ChevronUp, Zap, Shield, Clock,
  TrendingUp, Target, Award, CheckCircle, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

/* ============================================
   THEME & CONFIG
============================================ */
const theme = {
  name: "BYTRON",
  accent: "#C98225",
  accentLight: "#e5a84b",
  danger: "#d90647",
  bgFrom: "#07090f",
  bgTo: "#0b0f19",
};

const TELEGRAM = { 
  chat: "https://t.me/Bytron",
  channel: "https://t.me/Bytron_Channel",
  reviews: "https://t.me/bytronreview"
};

const INSTAGRAM = {
  url: "https://www.instagram.com/bytron_hack?igsh=YXhmY3dpc2Yxazg5",
};

/* ============================================
   UI HELPERS
============================================ */
const Button = ({ className = "", variant = "default", children, ...props }) => {
  const variants = {
    default: "bg-white/10 hover:bg-white/20",
    primary: `bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold`,
    danger: "bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500",
    outline: `border-2 border-amber-500 hover:bg-amber-500/20`,
  };
  
  return (
    <button
      className={`px-4 py-2 rounded-full transition-all duration-300 ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Card = ({ className = "", children, glow = false, ...props }) => (
  <div
    className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ${glow ? 'shadow-[0_0_30px_rgba(201,130,37,0.15)]' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

const Grad = ({ children }) => (
  <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
    {children}
  </span>
);

const SectionTitle = ({ subtitle, children }) => (
  <div className="text-center mb-8 sm:mb-12">
    {subtitle && (
      <p className="text-amber-400 text-sm font-medium mb-2 uppercase tracking-wider">{subtitle}</p>
    )}
    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">{children}</h2>
  </div>
);

/* ============================================
   COUNTRIES DATA
============================================ */
const COUNTRIES = [
  { name: "Afghanistan", code: "AF" },
  { name: "Albania", code: "AL" },
  { name: "Algeria", code: "DZ" },
  { name: "Andorra", code: "AD" },
  { name: "Angola", code: "AO" },
  { name: "Antigua and Barbuda", code: "AG" },
  { name: "Argentina", code: "AR" },
  { name: "Armenia", code: "AM" },
  { name: "Australia", code: "AU" },
  { name: "Austria", code: "AT" },
  { name: "Azerbaijan", code: "AZ" },
  { name: "Bahamas", code: "BS" },
  { name: "Bahrain", code: "BH" },
  { name: "Bangladesh", code: "BD" },
  { name: "Barbados", code: "BB" },
  { name: "Belarus", code: "BY" },
  { name: "Belgium", code: "BE" },
  { name: "Belize", code: "BZ" },
  { name: "Benin", code: "BJ" },
  { name: "Bhutan", code: "BT" },
  { name: "Bolivia", code: "BO" },
  { name: "Bosnia and Herzegovina", code: "BA" },
  { name: "Botswana", code: "BW" },
  { name: "Brazil", code: "BR" },
  { name: "Brunei", code: "BN" },
  { name: "Bulgaria", code: "BG" },
  { name: "Burkina Faso", code: "BF" },
  { name: "Burundi", code: "BI" },
  { name: "Cabo Verde", code: "CV" },
  { name: "Cambodia", code: "KH" },
  { name: "Cameroon", code: "CM" },
  { name: "Canada", code: "CA" },
  { name: "Central African Republic", code: "CF" },
  { name: "Chad", code: "TD" },
  { name: "Chile", code: "CL" },
  { name: "China", code: "CN" },
  { name: "Colombia", code: "CO" },
  { name: "Comoros", code: "KM" },
  { name: "Congo (Democratic Republic)", code: "CD" },
  { name: "Congo (Republic)", code: "CG" },
  { name: "Costa Rica", code: "CR" },
  { name: "Côte d'Ivoire", code: "CI" },
  { name: "Croatia", code: "HR" },
  { name: "Cuba", code: "CU" },
  { name: "Cyprus", code: "CY" },
  { name: "Czech Republic", code: "CZ" },
  { name: "Denmark", code: "DK" },
  { name: "Djibouti", code: "DJ" },
  { name: "Dominica", code: "DM" },
  { name: "Dominican Republic", code: "DO" },
  { name: "Ecuador", code: "EC" },
  { name: "Egypt", code: "EG" },
  { name: "El Salvador", code: "SV" },
  { name: "Equatorial Guinea", code: "GQ" },
  { name: "Eritrea", code: "ER" },
  { name: "Estonia", code: "EE" },
  { name: "Eswatini", code: "SZ" },
  { name: "Ethiopia", code: "ET" },
  { name: "Fiji", code: "FJ" },
  { name: "Finland", code: "FI" },
  { name: "France", code: "FR" },
  { name: "Gabon", code: "GA" },
  { name: "Gambia", code: "GM" },
  { name: "Georgia", code: "GE" },
  { name: "Germany", code: "DE" },
  { name: "Ghana", code: "GH" },
  { name: "Greece", code: "GR" },
  { name: "Grenada", code: "GD" },
  { name: "Guatemala", code: "GT" },
  { name: "Guinea", code: "GN" },
  { name: "Guinea-Bissau", code: "GW" },
  { name: "Guyana", code: "GY" },
  { name: "Haiti", code: "HT" },
  { name: "Holy See (Vatican City)", code: "VA" },
  { name: "Honduras", code: "HN" },
  { name: "Hungary", code: "HU" },
  { name: "Iceland", code: "IS" },
  { name: "India", code: "IN" },
  { name: "Indonesia", code: "ID" },
  { name: "Iran", code: "IR" },
  { name: "Iraq", code: "IQ" },
  { name: "Ireland", code: "IE" },
  { name: "Israel", code: "IL" },
  { name: "Italy", code: "IT" },
  { name: "Jamaica", code: "JM" },
  { name: "Japan", code: "JP" },
  { name: "Jordan", code: "JO" },
  { name: "Kazakhstan", code: "KZ" },
  { name: "Kenya", code: "KE" },
  { name: "Kiribati", code: "KI" },
  { name: "Korea (North)", code: "KP" },
  { name: "Korea (South)", code: "KR" },
  { name: "Kosovo", code: "XK" },
  { name: "Kuwait", code: "KW" },
  { name: "Kyrgyzstan", code: "KG" },
  { name: "Laos", code: "LA" },
  { name: "Latvia", code: "LV" },
  { name: "Lebanon", code: "LB" },
  { name: "Lesotho", code: "LS" },
  { name: "Liberia", code: "LR" },
  { name: "Libya", code: "LY" },
  { name: "Liechtenstein", code: "LI" },
  { name: "Lithuania", code: "LT" },
  { name: "Luxembourg", code: "LU" },
  { name: "Madagascar", code: "MG" },
  { name: "Malawi", code: "MW" },
  { name: "Malaysia", code: "MY" },
  { name: "Maldives", code: "MV" },
  { name: "Mali", code: "ML" },
  { name: "Malta", code: "MT" },
  { name: "Marshall Islands", code: "MH" },
  { name: "Mauritania", code: "MR" },
  { name: "Mauritius", code: "MU" },
  { name: "Mexico", code: "MX" },
  { name: "Micronesia", code: "FM" },
  { name: "Moldova", code: "MD" },
  { name: "Monaco", code: "MC" },
  { name: "Mongolia", code: "MN" },
  { name: "Montenegro", code: "ME" },
  { name: "Morocco", code: "MA" },
  { name: "Mozambique", code: "MZ" },
  { name: "Myanmar", code: "MM" },
  { name: "Namibia", code: "NA" },
  { name: "Nauru", code: "NR" },
  { name: "Nepal", code: "NP" },
  { name: "Netherlands", code: "NL" },
  { name: "New Zealand", code: "NZ" },
  { name: "Nicaragua", code: "NI" },
  { name: "Niger", code: "NE" },
  { name: "Nigeria", code: "NG" },
  { name: "North Macedonia", code: "MK" },
  { name: "Norway", code: "NO" },
  { name: "Oman", code: "OM" },
  { name: "Pakistan", code: "PK" },
  { name: "Palau", code: "PW" },
  { name: "Palestine", code: "PS" },
  { name: "Panama", code: "PA" },
  { name: "Papua New Guinea", code: "PG" },
  { name: "Paraguay", code: "PY" },
  { name: "Peru", code: "PE" },
  { name: "Philippines", code: "PH" },
  { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" },
  { name: "Qatar", code: "QA" },
  { name: "Romania", code: "RO" },
  { name: "Russia", code: "RU" },
  { name: "Rwanda", code: "RW" },
  { name: "Saint Kitts and Nevis", code: "KN" },
  { name: "Saint Lucia", code: "LC" },
  { name: "Saint Vincent and the Grenadines", code: "VC" },
  { name: "Samoa", code: "WS" },
  { name: "San Marino", code: "SM" },
  { name: "Sao Tome and Principe", code: "ST" },
  { name: "Saudi Arabia", code: "SA" },
  { name: "Senegal", code: "SN" },
  { name: "Serbia", code: "RS" },
  { name: "Seychelles", code: "SC" },
  { name: "Sierra Leone", code: "SL" },
  { name: "Singapore", code: "SG" },
  { name: "Slovakia", code: "SK" },
  { name: "Slovenia", code: "SI" },
  { name: "Solomon Islands", code: "SB" },
  { name: "Somalia", code: "SO" },
  { name: "South Africa", code: "ZA" },
  { name: "South Sudan", code: "SS" },
  { name: "Spain", code: "ES" },
  { name: "Sri Lanka", code: "LK" },
  { name: "Sudan", code: "SD" },
  { name: "Suriname", code: "SR" },
  { name: "Sweden", code: "SE" },
  { name: "Switzerland", code: "CH" },
  { name: "Syria", code: "SY" },
  { name: "Tajikistan", code: "TJ" },
  { name: "Tanzania", code: "TZ" },
  { name: "Thailand", code: "TH" },
  { name: "Timor-Leste", code: "TL" },
  { name: "Togo", code: "TG" },
  { name: "Tonga", code: "TO" },
  { name: "Trinidad and Tobago", code: "TT" },
  { name: "Tunisia", code: "TN" },
  { name: "Turkey", code: "TR" },
  { name: "Turkmenistan", code: "TM" },
  { name: "Tuvalu", code: "TV" },
  { name: "Uganda", code: "UG" },
  { name: "Ukraine", code: "UA" },
  { name: "United Arab Emirates", code: "AE" },
  { name: "United Kingdom", code: "GB" },
  { name: "United States", code: "US" },
  { name: "Uruguay", code: "UY" },
  { name: "Uzbekistan", code: "UZ" },
  { name: "Vanuatu", code: "VU" },
  { name: "Venezuela", code: "VE" },
  { name: "Vietnam", code: "VN" },
  { name: "Yemen", code: "YE" },
  { name: "Zambia", code: "ZM" },
  { name: "Zimbabwe", code: "ZW" },
];

/* ============================================
   LIVE PREDICTOR DEMO
============================================ */
function LivePredictorDemo() {
  const [isRunning, setIsRunning] = useState(false);
  const [currentMultiplier, setCurrentMultiplier] = useState(1.00);
  const [prediction, setPrediction] = useState(null);
  const [history, setHistory] = useState([
    { mult: 2.47, win: true },
    { mult: 1.32, win: false },
    { mult: 8.91, win: true },
    { mult: 3.24, win: true },
    { mult: 1.05, win: false },
    { mult: 12.67, win: true },
    { mult: 2.89, win: true },
    { mult: 1.78, win: true },
  ]);
  const [status, setStatus] = useState("idle"); // idle, analyzing, predicted, flying, crashed
  const intervalRef = useRef(null);

  const generatePrediction = () => {
    // Generate a random prediction between 1.5 and 15
    const pred = (Math.random() * 13.5 + 1.5).toFixed(2);
    return parseFloat(pred);
  };

  const startPrediction = () => {
    setIsRunning(true);
    setStatus("analyzing");
    setCurrentMultiplier(1.00);
    
    // Analyzing phase
    setTimeout(() => {
      const pred = generatePrediction();
      setPrediction(pred);
      setStatus("predicted");
      
      // Start flying after showing prediction
      setTimeout(() => {
        setStatus("flying");
        let mult = 1.00;
        const crashPoint = pred + (Math.random() * 2 - 1); // Crash near prediction
        
        intervalRef.current = setInterval(() => {
          mult += 0.01 + Math.random() * 0.05;
          setCurrentMultiplier(parseFloat(mult.toFixed(2)));
          
          if (mult >= crashPoint) {
            clearInterval(intervalRef.current);
            setStatus("crashed");
            setHistory(prev => [
              { mult: parseFloat(mult.toFixed(2)), win: mult >= pred * 0.8 },
              ...prev.slice(0, 7)
            ]);
            
            setTimeout(() => {
              setIsRunning(false);
              setStatus("idle");
              setPrediction(null);
              setCurrentMultiplier(1.00);
            }, 2000);
          }
        }, 50);
      }, 1500);
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="Live Demo">
        Try Our <Grad>Predictor</Grad> Now
      </SectionTitle>
      
      <div className="max-w-4xl mx-auto">
        <Card glow className="overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/20 to-red-500/20 p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                <span className="font-semibold">BYTRON Predictor v4.0</span>
              </div>
              <div className="text-sm text-white/60">
                Demo Mode
              </div>
            </div>
          </div>
          
          {/* Main Display */}
          <div className="p-6 sm:p-8">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Multiplier Display */}
              <div className="text-center">
                <div 
                  className={`text-6xl sm:text-8xl font-bold mb-4 transition-colors duration-300 ${
                    status === "crashed" ? "text-red-500" : 
                    status === "flying" ? "text-green-400" : 
                    "text-white"
                  }`}
                >
                  {currentMultiplier.toFixed(2)}x
                </div>
                
                {/* Prediction Display */}
                {prediction && status !== "idle" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4"
                  >
                    <div className="text-sm text-white/60 mb-1">Predicted Cashout:</div>
                    <div className="text-2xl font-bold text-amber-400">
                      {prediction.toFixed(2)}x
                    </div>
                  </motion.div>
                )}
                
                {/* Status */}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
                  status === "analyzing" ? "bg-blue-500/20 text-blue-400" :
                  status === "predicted" ? "bg-amber-500/20 text-amber-400" :
                  status === "flying" ? "bg-green-500/20 text-green-400" :
                  status === "crashed" ? "bg-red-500/20 text-red-400" :
                  "bg-white/10 text-white/60"
                }`}>
                  {status === "analyzing" && (
                    <>
                      <div className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                      Analyzing patterns...
                    </>
                  )}
                  {status === "predicted" && (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Prediction Ready!
                    </>
                  )}
                  {status === "flying" && (
                    <>
                      <TrendingUp className="h-4 w-4" />
                      Flying...
                    </>
                  )}
                  {status === "crashed" && (
                    <>
                      <X className="h-4 w-4" />
                      Crashed at {currentMultiplier.toFixed(2)}x
                    </>
                  )}
                  {status === "idle" && "Ready to predict"}
                </div>
                
                {/* Start Button */}
                <div className="mt-6">
                  <Button
                    variant="primary"
                    className="px-8 py-3 text-lg"
                    onClick={startPrediction}
                    disabled={isRunning}
                  >
                    {isRunning ? "Running..." : "Start Prediction"}
                  </Button>
                </div>
              </div>
              
              {/* History */}
              <div>
                <div className="text-sm text-white/60 mb-3">Recent Results:</div>
                <div className="grid grid-cols-4 gap-2">
                  {history.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className={`p-3 rounded-lg text-center font-bold ${
                        item.mult >= 5 ? "bg-purple-500/20 text-purple-400" :
                        item.mult >= 2 ? "bg-green-500/20 text-green-400" :
                        "bg-blue-500/20 text-blue-400"
                      }`}
                    >
                      {item.mult.toFixed(2)}x
                    </motion.div>
                  ))}
                </div>
                
                {/* Stats */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">94%</div>
                    <div className="text-xs text-white/60">Accuracy</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-amber-400">2.8x</div>
                    <div className="text-xs text-white/60">Avg Win</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-purple-400">1.2K</div>
                    <div className="text-xs text-white/60">Users</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-white/5 p-4 border-t border-white/10 text-center">
            <p className="text-sm text-white/60">
              This is a demo. Get the full version for real predictions.
            </p>
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => window.open(TELEGRAM.chat, "_blank")}
            >
              <Download className="h-4 w-4 mr-2" />
              Get Full Version
            </Button>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ============================================
   HISTORY CHART
============================================ */
function HistoryChart() {
  const [data] = useState(() => {
    // Generate fake history data
    const arr = [];
    for (let i = 0; i < 30; i++) {
      arr.push({
        time: `${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        multiplier: (Math.random() * 20 + 1).toFixed(2),
      });
    }
    return arr;
  });

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
      <SectionTitle subtitle="Live Statistics">
        Game <Grad>History</Grad>
      </SectionTitle>
      
      <Card className="p-4 sm:p-6 overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-white/60">Live Feed</span>
          </div>
          <div className="text-sm text-white/60">Last 30 rounds</div>
        </div>
        
        {/* Scrollable History */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {data.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`flex-shrink-0 p-3 rounded-lg text-center min-w-[70px] ${
                parseFloat(item.multiplier) >= 10 ? "bg-purple-500/20 border border-purple-500/30" :
                parseFloat(item.multiplier) >= 5 ? "bg-green-500/20 border border-green-500/30" :
                parseFloat(item.multiplier) >= 2 ? "bg-blue-500/20 border border-blue-500/30" :
                "bg-white/5 border border-white/10"
              }`}
            >
              <div className={`font-bold ${
                parseFloat(item.multiplier) >= 10 ? "text-purple-400" :
                parseFloat(item.multiplier) >= 5 ? "text-green-400" :
                parseFloat(item.multiplier) >= 2 ? "text-blue-400" :
                "text-white/70"
              }`}>
                {item.multiplier}x
              </div>
              <div className="text-xs text-white/40 mt-1">{item.time}</div>
            </motion.div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/10 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-purple-500/50" />
            <span className="text-white/60">10x+ (Mega Win)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-green-500/50" />
            <span className="text-white/60">5x-10x (Big Win)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-blue-500/50" />
            <span className="text-white/60">2x-5x (Win)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-white/20" />
            <span className="text-white/60">Below 2x</span>
          </div>
        </div>
      </Card>
    </section>
  );
}

/* ============================================
   HOW IT WORKS
============================================ */
function HowItWorks() {
  const steps = [
    {
      icon: <Download className="h-6 w-6" />,
      title: "1. Get the App",
      desc: "Contact us on Telegram to get the predictor app for your device."
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "2. Connect",
      desc: "Open the game and run our predictor alongside it."
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "3. Get Signals",
      desc: "Receive real-time predictions before each round starts."
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "4. Win",
      desc: "Cash out at the predicted multiplier and collect your winnings."
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="Simple Process">
        How It <Grad>Works</Grad>
      </SectionTitle>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-6 h-full hover:border-amber-500/50 transition-colors">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-red-500/20 flex items-center justify-center text-amber-400 mb-4">
                {step.icon}
              </div>
              <h3 className="font-bold text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-white/60">{step.desc}</p>
            </Card>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ============================================
   FAQ SECTION
============================================ */
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: "How does the predictor work?",
      a: "Our predictor uses advanced algorithms to analyze game patterns, server seeds, and historical data to predict the next crash point. It works by intercepting and analyzing the game's data stream in real-time."
    },
    {
      q: "Which games are supported?",
      a: "We support Aviator, Crash, LuckyJet, Mines, Apple of Fortune, Thimbles, and more. Our predictor works on platforms like 1win, 1xbet, Mostbet, and others."
    },
    {
      q: "Is it safe to use?",
      a: "Yes, our predictor runs locally on your device and doesn't interact with the game server. It only analyzes publicly available data. We've had thousands of users without any account issues."
    },
    {
      q: "What's the accuracy rate?",
      a: "Our predictor maintains a 94%+ accuracy rate. However, we recommend using proper bankroll management and not betting more than you can afford to lose."
    },
    {
      q: "How do I get the app?",
      a: "Contact us on Telegram @Bytron to purchase the app. After payment, you'll receive the download link and activation key instantly."
    },
    {
      q: "Can I use it on multiple devices?",
      a: "Each license is for one device only. If you need to use it on multiple devices, you'll need to purchase additional licenses."
    },
    {
      q: "Is there a refund policy?",
      a: "Due to the digital nature of our product, we don't offer refunds. However, we provide free support and updates to ensure the predictor works correctly."
    },
    {
      q: "How long does the license last?",
      a: "Your license is lifetime with free updates. Once you purchase, you have access forever."
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="Common Questions">
        Frequently Asked <Grad>Questions</Grad>
      </SectionTitle>
      
      <div className="max-w-3xl mx-auto space-y-3">
        {faqs.map((faq, i) => (
          <Card 
            key={i}
            className={`overflow-hidden transition-colors ${openIndex === i ? 'border-amber-500/50' : ''}`}
          >
            <button
              className="w-full p-4 sm:p-5 flex items-center justify-between text-left"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <span className="font-medium pr-4">{faq.q}</span>
              {openIndex === i ? (
                <ChevronUp className="h-5 w-5 text-amber-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/40 flex-shrink-0" />
              )}
            </button>
            <AnimatePresence>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-white/70 text-sm leading-relaxed border-t border-white/10 pt-4">
                    {faq.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* ============================================
   SERVER STATUS MODAL
============================================ */
function ServerStatusModal({ isOpen, onClose }) {
  const [search, setSearch] = useState("");

  if (!isOpen) return null;

  const filteredCountries = COUNTRIES.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ type: "tween", duration: 0.3 }}
        className="relative w-full sm:max-w-2xl max-h-[90vh] sm:max-h-[80vh] rounded-t-3xl sm:rounded-2xl border-t-2 sm:border-2 overflow-hidden"
        style={{ borderColor: theme.accent, backgroundColor: "#0a0c14" }}
      >
        <div className="sticky top-0 z-10 p-4 border-b border-white/10" style={{ backgroundColor: "#0a0c14" }}>
          <div className="flex justify-center mb-3 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-white/30" />
          </div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-amber-400" />
              <h2 className="text-lg sm:text-xl font-bold">Server Availability</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
            <input
              type="text"
              placeholder="Search your country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:border-amber-400/50 text-base"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div className="mt-3 flex items-center gap-4 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span>{COUNTRIES.length} servers online</span>
            </div>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] sm:max-h-[calc(80vh-180px)] p-4 overscroll-contain">
          <div className="grid grid-cols-1 gap-2">
            {filteredCountries.map((country) => (
              <div
                key={country.code}
                className="flex items-center justify-between p-3 rounded-xl bg-white/5 active:bg-white/10 sm:hover:bg-white/10 transition"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                    alt={country.name}
                    className="w-6 h-4 object-cover rounded-sm"
                  />
                  <span className="text-sm">{country.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                  <span className="text-xs text-green-400">Online</span>
                </div>
              </div>
            ))}
          </div>
          {filteredCountries.length === 0 && (
            <div className="text-center py-8 text-white/50">
              No countries found matching "{search}"
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* ============================================
   NAVIGATION
============================================ */
function GlassNav({ onOpenServerStatus }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "Demo", href: "#demo" },
    { label: "Catalog", href: "#catalog" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-40">
        <div className="mx-auto max-w-7xl px-3 py-2 sm:px-4 sm:py-3">
          <div
            className="flex items-center justify-between rounded-full border-2 backdrop-blur px-3 py-2 sm:px-4"
            style={{ borderColor: theme.accent, backgroundColor: "rgba(1,3,13,0.85)" }}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-xl sm:rounded-2xl overflow-hidden"
                style={{
                  background: "radial-gradient(120% 120% at 50% 0%, rgba(201,130,37,.25), transparent)",
                  boxShadow: "0 0 25px -6px #C98225",
                  border: "2px solid rgba(201,130,37,.65)",
                }}
              >
                <img src="/logo.png" alt="BYTRON logo" className="absolute inset-0 h-full w-full object-cover" draggable="false" />
              </div>
              <div className="text-sm sm:text-base font-semibold tracking-wider">{theme.name}</div>
            </div>

            <div className="hidden items-center gap-2 md:flex">
              {navLinks.map((link) => (
                <a key={link.label} className="nav-btn" href={link.href}>{link.label}</a>
              ))}
              <button onClick={onOpenServerStatus} className="nav-btn flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                Servers
              </button>
              <Button variant="primary" onClick={() => window.open(TELEGRAM.chat, "_blank")}>
                Get Started
              </Button>
            </div>

            <button
              className="md:hidden p-2 -mr-1 rounded-full hover:bg-white/10 transition"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-72 md:hidden"
              style={{ backgroundColor: "#0a0c14" }}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <span className="font-semibold">Menu</span>
                  <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex-1 p-4 space-y-2">
                  {navLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block w-full p-3 rounded-xl text-left hover:bg-white/10 transition"
                    >
                      {link.label}
                    </a>
                  ))}
                  <button
                    onClick={() => { setMobileMenuOpen(false); onOpenServerStatus(); }}
                    className="flex items-center gap-3 w-full p-3 rounded-xl text-left hover:bg-white/10 transition"
                  >
                    <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />
                    Server Status
                  </button>
                </div>
                <div className="p-4 border-t border-white/10">
                  <button
                    onClick={() => window.open(TELEGRAM.chat, "_blank")}
                    className="w-full py-3 rounded-xl font-semibold transition bg-gradient-to-r from-amber-500 to-amber-600 text-black"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ============================================
   ACTIVE USERS COUNTER
============================================ */
function ActiveUsers() {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const startDate = new Date('2026-01-01').getTime();
    const now = Date.now();
    const daysPassed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    const baseCount = 892 + Math.floor(daysPassed * 12.5);
    const variation = Math.floor(Math.random() * 20);
    const finalCount = baseCount + variation;
    
    let current = 0;
    const target = finalCount;
    const duration = 1500;
    const steps = 60;
    const increment = target / steps;
    const stepTime = duration / steps;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, stepTime);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="flex items-center justify-center"
    >
      <div 
        className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-full border"
        style={{ borderColor: 'rgba(201, 130, 37, 0.4)', backgroundColor: 'rgba(201, 130, 37, 0.1)' }}
      >
        <div className="relative">
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />
          <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="text-lg sm:text-xl font-bold text-white">{count.toLocaleString()}</span>
          <span className="text-xs sm:text-sm text-white/60">active users</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================
   HERO
============================================ */
function Hero() {
  return (
    <section className="relative isolate mx-auto flex min-h-[85vh] sm:min-h-[92vh] max-w-7xl flex-col items-center justify-center gap-4 sm:gap-6 px-4 pt-20 sm:pt-28 pb-8 text-center">
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-sm"
        style={{ borderColor: theme.accent, backgroundColor: 'rgba(201, 130, 37, 0.1)' }}
      >
        <Sparkles className="h-4 w-4 text-amber-400" />
        <span>v4.0 Now Available</span>
      </motion.div>

      {/* Title */}
      <motion.h1 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-3xl sm:text-5xl md:text-7xl font-bold leading-tight"
      >
        <Grad>Aviator Predictor</Grad>
        <br />
        <span className="text-2xl sm:text-4xl md:text-5xl text-white/90">AI-Powered Game Analysis</span>
      </motion.h1>

      {/* Subtitle */}
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mx-auto max-w-2xl text-white/70 text-sm sm:text-base md:text-lg"
      >
        Advanced prediction algorithms for Aviator, Crash, LuckyJet, Mines and more.
        Works on 1win, 1xbet, Mostbet and 190+ countries.
      </motion.p>

      {/* CTA Buttons */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto"
      >
        <Button 
          variant="primary"
          className="w-full sm:w-auto px-8 py-3 text-lg flex items-center justify-center gap-2"
          onClick={() => window.open(TELEGRAM.chat, "_blank")}
        >
          <Download className="h-5 w-5" />
          Download Now
        </Button>
        <Button 
          variant="outline"
          className="w-full sm:w-auto px-6 py-3"
          onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
        >
          Try Demo
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </motion.div>

      {/* Active Users */}
      <div className="mt-4">
        <ActiveUsers />
      </div>

      {/* Trust Badges */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-white/50 text-xs sm:text-sm"
      >
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-green-400" />
          <span>Safe & Secure</span>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-400" />
          <span>94% Accuracy</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-400" />
          <span>24/7 Support</span>
        </div>
      </motion.div>
    </section>
  );
}

/* ============================================
   FEATURES
============================================ */
function Features() {
  const features = [
    {
      icon: <Target className="h-6 w-6" />,
      title: "High Accuracy",
      desc: "94%+ prediction accuracy using advanced AI algorithms and pattern recognition.",
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "190+ Countries",
      desc: "Works worldwide with dedicated servers in every major region.",
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Undetectable",
      desc: "Runs locally on your device. No interaction with game servers.",
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Real-time Signals",
      desc: "Get predictions before each round starts. Never miss a winning opportunity.",
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "24/7 Support",
      desc: "Our team is always available to help you with any issues.",
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Lifetime Access",
      desc: "One-time payment for lifetime access with free updates.",
    },
  ];

  return (
    <section id="features" className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="Why Choose Us">
        Powerful <Grad>Features</Grad>
      </SectionTitle>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {features.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-5 sm:p-6 h-full hover:border-amber-500/30 transition-colors group">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-red-500/20 flex items-center justify-center text-amber-400 mb-4 group-hover:scale-110 transition-transform">
                {f.icon}
              </div>
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-white/60">{f.desc}</p>
            </Card>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ============================================
   CATALOG
============================================ */
function ProductCard({ title, price, image }) {
  return (
    <Link to={`/product/${title}`} className="block w-full cursor-pointer active:scale-[0.98] transition-transform">
      <div
        className="rounded-2xl border-2 sm:border-4 p-2 sm:p-3 transition hover:scale-[1.01] hover:border-amber-400"
        style={{ borderColor: theme.accent }}
      >
        <div className="overflow-hidden rounded-lg sm:rounded-xl">
          <div className="aspect-[16/9] relative bg-gradient-to-br from-white/10 to-transparent">
            <img src={image} className="absolute inset-0 h-full w-full object-cover" alt={title} />
          </div>
        </div>
        <div className="mt-2 sm:mt-3 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-sm sm:text-lg font-semibold truncate">{title}</div>
            <div className="text-xs text-white/50">BYTRON</div>
          </div>
          <div className="rounded-full border-2 px-3 sm:px-4 py-1 text-sm ml-2 flex-shrink-0" style={{ borderColor: theme.accent }}>
            {price}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Catalog() {
  const items = [
    { title: "Crash", price: "$100", image: "/1xbet-crash.png" },
    { title: "Aviator Spribe", price: "$100", image: "/1win-aviator.png" },
    { title: "LuckyJet", price: "$100", image: "/1win-luckyjet.png" },
    { title: "Mines", price: "$100", image: "/mines.png" },
    { title: "Mostbet Aviator Spribe", price: "$100", image: "/mostbet-aviator.png" },
    { title: "Apple of Fortune", price: "$100", image: "/apple-of-fortune.png" },
    { title: "Thimbles", price: "$100", image: "/thimbles.png" },
    { title: "Wild West Gold", price: "$100", image: "/wild-west-gold.png" },
    { title: "Higher vs Lower", price: "$100", image: "/higher-vs-lower.png" },
    { title: "Dragons Gold", price: "$100", image: "/dragons-gold.png" },
  ];

  return (
    <section id="catalog" className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
      <SectionTitle subtitle="Our Products">
        BYTRON <Grad>Catalog</Grad>
      </SectionTitle>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
        {items.map((it) => (
          <ProductCard key={it.title} {...it} />
        ))}
      </div>
    </section>
  );
}

/* ============================================
   VIDEO SECTION
============================================ */
function VideoSection() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
      <SectionTitle subtitle="See It In Action">
        Watch <Grad>Demo</Grad>
      </SectionTitle>

      <div className="max-w-3xl mx-auto">
        <Card glow className="overflow-hidden">
          <div className="aspect-video">
            <iframe
              src="https://www.youtube.com/embed/of9J1gFBHGA?rel=0&modestbranding=1"
              title="BYTRON Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ============================================
   REVIEWS
============================================ */
function Reviews() {
  return (
    <section id="reviews" className="mx-auto max-w-7xl px-4 py-12 sm:py-16 text-center">
      <SectionTitle subtitle="Testimonials">
        Community <Grad>Reviews</Grad>
      </SectionTitle>

      <div className="flex justify-center">
        <Button
          variant="outline"
          className="text-sm sm:text-lg px-6 sm:px-8 py-3 sm:py-4 flex items-center gap-2 sm:gap-3"
          onClick={() => window.open(TELEGRAM.reviews, "_blank")}
        >
          <img src="/icons/telegram.svg" className="h-4 w-4 sm:h-5 sm:w-5" alt="" />
          View 500+ Reviews on Telegram
        </Button>
      </div>
    </section>
  );
}

/* ============================================
   CTA
============================================ */
function CTA() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20 text-center">
      <Card glow className="p-8 sm:p-12">
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
          Ready to Start <Grad>Winning</Grad>?
        </h3>
        <p className="mx-auto max-w-xl text-white/70 mb-6">
          Join thousands of users who are already using BYTRON to predict game outcomes.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button variant="primary" className="px-8 py-3 text-lg" onClick={() => window.open(TELEGRAM.chat, "_blank")}>
            <Download className="h-5 w-5 mr-2" />
            Get Started Now
          </Button>
          <Button variant="outline" className="px-6 py-3" onClick={() => window.open(TELEGRAM.channel, "_blank")}>
            Join Channel
          </Button>
        </div>
      </Card>
    </section>
  );
}

/* ============================================
   FOOTER
============================================ */
function Footer() {
  return (
    <footer className="mx-auto max-w-7xl px-4 pb-8 sm:pb-12 pt-8 border-t border-white/10">
      <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="BYTRON" className="h-8 w-8 rounded-lg" />
            <span className="font-bold text-lg">{theme.name}</span>
          </div>
          <p className="text-sm text-white/60">
            Advanced AI-powered game prediction tools. Trusted by thousands worldwide.
          </p>
        </div>

        <div className="text-sm">
          <div className="font-semibold mb-3">Quick Links</div>
          <ul className="space-y-2 text-white/60">
            <li><a className="hover:text-white transition" href="#features">Features</a></li>
            <li><a className="hover:text-white transition" href="#demo">Demo</a></li>
            <li><a className="hover:text-white transition" href="#catalog">Catalog</a></li>
            <li><a className="hover:text-white transition" href="#faq">FAQ</a></li>
          </ul>
        </div>

        <div className="text-sm">
          <div className="font-semibold mb-3">Products</div>
          <ul className="space-y-2 text-white/60">
            <li><Link className="hover:text-white transition" to="/product/Crash">Crash Predictor</Link></li>
            <li><Link className="hover:text-white transition" to="/product/Aviator Spribe">Aviator Predictor</Link></li>
            <li><Link className="hover:text-white transition" to="/product/LuckyJet">LuckyJet Predictor</Link></li>
            <li><Link className="hover:text-white transition" to="/product/Mines">Mines Predictor</Link></li>
          </ul>
        </div>

        <div className="text-sm">
          <div className="font-semibold mb-3">Contact</div>
          <ul className="space-y-2 text-white/60">
            <li className="flex items-center gap-2">
              <img src="/icons/telegram.svg" className="h-4 w-4 opacity-60" alt="" />
              <a className="hover:text-white transition" href={TELEGRAM.chat} target="_blank" rel="noopener noreferrer">@Bytron</a>
            </li>
            <li className="flex items-center gap-2">
              <img src="/icons/telegram.svg" className="h-4 w-4 opacity-60" alt="" />
              <a className="hover:text-white transition" href={TELEGRAM.channel} target="_blank" rel="noopener noreferrer">Channel</a>
            </li>
            <li className="flex items-center gap-2">
              <img src="/icons/instagram.svg" className="h-4 w-4 opacity-60" alt="" />
              <a className="hover:text-white transition" href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer">Instagram</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
        <div>© {new Date().getFullYear()} BYTRON. All rights reserved.</div>
        <div className="flex items-center gap-4">
          <span>190+ Countries Supported</span>
          <span>•</span>
          <span>24/7 Support</span>
        </div>
      </div>
    </footer>
  );
}

/* ============================================
   MAIN APP
============================================ */
export default function App() {
  const [serverStatusOpen, setServerStatusOpen] = useState(false);

  return (
    <div
      className="min-h-screen w-full text-white antialiased"
      style={{ background: `linear-gradient(180deg, ${theme.bgFrom}, ${theme.bgTo})` }}
    >
      <GlassNav onOpenServerStatus={() => setServerStatusOpen(true)} />
      <Hero />
      <Features />
      <div id="demo">
        <LivePredictorDemo />
      </div>
      <HistoryChart />
      <HowItWorks />
      <Catalog />
      <VideoSection />
      <Reviews />
      <div id="faq">
        <FAQ />
      </div>
      <CTA />
      <Footer />

      <ServerStatusModal isOpen={serverStatusOpen} onClose={() => setServerStatusOpen(false)} />
    </div>
  );
}
